import { Skeleton } from "@/components/ui/skeleton";
import DiaryCharts from "@/features/charts/DiaryCharts";
import { diaryByIdQueryOptions } from "@/features/diaries/api";
import History from "@/features/diaries/features/history/components/History";
import { collateDiaryHistory } from "@/features/diaries/services/collateDiaryHistory";
import DiaryCard from "@/features/patients/features/interface/components/DiaryCard";
import PatientCard from "@/features/patients/features/patient/PatientCard";
import {
	selectProfileByIdQueryOptions,
	useProfileByIdQuery,
} from "@/features/profile/api/selectProfile";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense, useEffect } from "react";
import { Button } from "../../../../../../components/ui/button";

const LoadingSkeleton = () => (
	<div className="space-y-4">
		<Skeleton className="w-full h-[200px]" />
		<Skeleton className="w-full h-[200px]" />
		<Skeleton className="w-full h-[400px]" />
		<Skeleton className="w-full h-[300px]" />
	</div>
);

const DiaryPreviewContent = () => {
	const { patientId, diaryId } = Route.useParams();
	const diaryQuery = useSuspenseQuery({
		...diaryByIdQueryOptions(diaryId),
		retry: true,
		retryDelay: (attemptIndex) => Math.min(500 * 2 ** attemptIndex, 10000),
		refetchOnWindowFocus: true,
		refetchOnMount: true,
		refetchOnReconnect: true,
		staleTime: 0,
	});
	const profileQuery = useSuspenseQuery({
		...selectProfileByIdQueryOptions(patientId),
		retry: true,
		retryDelay: (attemptIndex) => Math.min(500 * 2 ** attemptIndex, 10000),
		refetchOnWindowFocus: true,
		refetchOnMount: true,
		refetchOnReconnect: true,
		staleTime: 0,
	});

	useEffect(() => {
		if (!diaryQuery.data) {
			diaryQuery.refetch();
		}
		if (!profileQuery.data) {
			profileQuery.refetch();
		}
	}, [
		diaryQuery.data,
		profileQuery.data,
		diaryQuery.refetch,
		profileQuery.refetch,
	]);

	// Show loading state while refetching
	if (!diaryQuery.data || !profileQuery.data) {
		return <LoadingSkeleton />;
	}

	return (
		<>
			<PatientCard editable={false} profile={profileQuery.data} />
			<DiaryCard editable={false} diary={diaryQuery.data} />
			<DiaryCharts disableAnimations={true} diary={diaryQuery.data} />
			<History
				size="sm"
				editable={false}
				addable={false}
				diaryId={diaryQuery.data.diary_id}
				diaryHistory={collateDiaryHistory(diaryQuery.data)}
			/>
		</>
	);
};

export const Route = createFileRoute(
	"/preview/patient/$patientId/diary/$diaryId/",
)({
	component: () => (
		<Suspense fallback={<LoadingSkeleton />}>
			<DiaryPreviewContent />
		</Suspense>
	),
	loader: async ({ context, params }) => {
		const { diaryId, patientId } = params as {
			diaryId: string;
			patientId: string;
		};
		try {
			// Prefetch both queries
			await Promise.all([
				context?.queryClient.prefetchQuery(diaryByIdQueryOptions(diaryId)),
				context?.queryClient.prefetchQuery(
					selectProfileByIdQueryOptions(patientId),
				),
			]);
		} catch (error) {
			console.error("Failed to prefetch data:", error);
			// Don't throw here, let the component handle the error state
		}
	},
	pendingComponent: LoadingSkeleton,
});
